import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import './TeamManagement.css';
import { useRevit } from '../../contexts/RevitContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserMinus, faUserPlus } from '@fortawesome/free-solid-svg-icons';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'revenue-node-server-tuomaskivioja-tuomaskiviojas-projects.vercel.app';

const TeamManagement = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState(['main']);
  const [teamMembers, setTeamMembers] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pendingChanges, setPendingChanges] = useState({});
  const [savingAccess, setSavingAccess] = useState({});
  const { getToken } = useAuth();
  const { username } = useRevit();

  const handleAccountSelectionChange = (accountId, isChecked) => {
    setSelectedAccounts(prev => {
      if (isChecked) {
        return [...prev, accountId];
      } else {
        return prev.filter(id => id !== accountId);
      }
    });
  };

  // Fetch team members and sub-accounts on component mount
  useEffect(() => {
    fetchTeamMembers();
    fetchSubAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubAccounts = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `https://${SERVER_URL}/api/get-sub-accounts/${username}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      console.log(response.data.subAccounts);

      if (response.data.hasSubAccounts) {
        setSubAccounts(response.data.subAccounts || []);
      }
    } catch (err) {
      console.error('Error fetching sub-accounts:', err);
    }
  };

  const handleSubAccountSelectionChange = (memberEmail, selectedSubAccounts) => {
    setPendingChanges(prev => ({
      ...prev,
      [memberEmail]: selectedSubAccounts
    }));
  };

  const handleSaveSubAccountAccess = async (memberEmail) => {
    const selectedSubAccounts = pendingChanges[memberEmail];
    setSavingAccess(prev => ({ ...prev, [memberEmail]: true }));
    
    try {
      const token = await getToken();
      const response = await axios.post(
        `https://${SERVER_URL}/api/update-team-member-access`,
        {
          primaryUser: username,
          email: memberEmail,
          subAccountAccess: selectedSubAccounts
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessage('Sub-account access updated successfully');
        fetchTeamMembers(); // Refresh to get updated data
        setPendingChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[memberEmail];
          return newChanges;
        });
      } else {
        setError(response.data.message || 'Failed to update sub-account access');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while updating access');
      console.error('Error updating sub-account access:', err);
    } finally {
      setSavingAccess(prev => ({ ...prev, [memberEmail]: false }));
    }
  };

  const getCurrentSelection = (member) => {
    return pendingChanges[member.email] !== undefined 
      ? pendingChanges[member.email] 
      : member.sub_account_access === null 
        ? ['main', ...subAccounts.map(account => account.username)]
        : member.sub_account_access || [];
  };

  const hasUnsavedChanges = (member) => {
    return pendingChanges[member.email] !== undefined;
  };

  const fetchTeamMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `https://${SERVER_URL}/api/team-members`,
        {
          params: { primaryUser: username },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      console.log(response.data.teamMembers);
      if (response.data.success) {
        setTeamMembers(response.data.teamMembers || []);
      } else {
        setError('Failed to fetch team members');
      }
    } catch (err) {
      setError('An error occurred while fetching team members');
      console.error('Error fetching team members:', err);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setError('');

    // Validate that at least one account is selected
    if (selectedAccounts.length === 0) {
      setError('At least 1 account should be selected for access for this team member');
      setIsLoading(false);
      return;
    }

    try {
      const token = await getToken();
      const response = await axios.post(
        `https://${SERVER_URL}/api/create-team-member`,
        {
          primaryUser: username,
          email: email,
          firstName: firstName,
          subAccountAccess: selectedAccounts
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessage('Invitation sent successfully!');
        setEmail('');
        setFirstName('');
        setSelectedAccounts(['main']);
        // Refresh the team members list
        fetchTeamMembers();
      } else {
        setError(response.data.message || 'Failed to send invitation');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while sending the invitation');
      console.error('Error inviting team member:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeAccess = async (memberEmail) => {
    try {
      const token = await getToken();
      const response = await axios.post(
        `https://${SERVER_URL}/api/remove-team-member`,
        {
          primaryUser: username,
          email: memberEmail
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessage('Access revoked successfully');
        // Refresh the team members list
        fetchTeamMembers();
      } else {
        setError(response.data.message || 'Failed to revoke access');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while revoking access');
      console.error('Error revoking access:', err);
    }
  };

  return (
    <div className="team-management-container">
      <h2>Team Management</h2>
      
      {/* Team Members List */}
      <div className="team-members-section">
        <h3>Team Members</h3>
        {isLoadingMembers ? (
          <p>Loading team members...</p>
        ) : teamMembers.length > 0 ? (
          <div className="team-members-list">
            {teamMembers.map((member, index) => (
              <div key={index} className="team-member-item">
                <div className="member-header">
                  <div className="member-info">
                    <span className="member-name">{member.name}</span>
                    <span className="member-email">{member.email}</span>
                    <span className="member-joined">Joined: {new Date(member.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className='sub-account-controls-container'>
                {subAccounts.length > 0 && (
                  <div className="sub-account-access">
                    <label>Account Access:</label>
                    <div className="sub-account-controls">
                      <div className="sub-account-checkboxes">
                        <label className="sub-account-checkbox">
                          <input
                            type="checkbox"
                            value="main"
                            checked={getCurrentSelection(member).includes('main')}
                            onChange={(e) => {
                              const currentSelection = getCurrentSelection(member);
                              const newSelection = e.target.checked
                                ? [...currentSelection, 'main']
                                : currentSelection.filter(username => username !== 'main');
                              handleSubAccountSelectionChange(member.email, newSelection);
                            }}
                          />
                          <span>Main Account</span>
                        </label>
                        {subAccounts.map(subAccount => (
                          <label key={subAccount.id} className="sub-account-checkbox">
                            <input
                              type="checkbox"
                              value={subAccount.username}
                              checked={getCurrentSelection(member).includes(subAccount.username)}
                              onChange={(e) => {
                                const currentSelection = getCurrentSelection(member);
                                const newSelection = e.target.checked
                                  ? [...currentSelection, subAccount.username]
                                  : currentSelection.filter(username => username !== subAccount.username);
                                handleSubAccountSelectionChange(member.email, newSelection);
                              }}
                            />
                            <span>{subAccount.name}</span>
                          </label>
                        ))}
                      </div>
                      {hasUnsavedChanges(member) && (
                        <button 
                          className="save-access-button"
                          onClick={() => handleSaveSubAccountAccess(member.email)}
                          disabled={savingAccess[member.email]}
                        >
                          {savingAccess[member.email] ? 'Saving...' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                <button 
                  className="revoke-button"
                  onClick={() => handleRevokeAccess(member.email)}
                >
                  <FontAwesomeIcon icon={faUserMinus} /> Revoke Access
                </button>
              </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-members">No team members found</p>
        )}
      </div>
      
      {/* Invite Form */}
      <div className="invite-section">
        <h3>Invite New Team Member</h3>
        <p className="invite-description">
          Team members will have access to your account data
        </p>
        
        <form onSubmit={handleInvite}>
          <div className="form-group-invite">
            <label className="sub-account-access-label" htmlFor="team-first-name">First Name</label>
            <input
              id="team-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter first name"
              required
            />
          </div>
          
          <div className="form-group-invite">
            <label className="sub-account-access-label" htmlFor="team-email">Email Address</label>
            <input
              id="team-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              required
            />
          </div>
          
          <div className="form-group-invite">
            <label className="sub-account-access-label">Account Access</label>
            <div className="sub-account-checkboxes">
              <label className="sub-account-checkbox">
                <input
                  type="checkbox"
                  value="main"
                  checked={selectedAccounts.includes('main')}
                  onChange={(e) => handleAccountSelectionChange('main', e.target.checked)}
                />
                <span>Main Account</span>
              </label>
              {subAccounts.map(subAccount => (
                <label key={subAccount.id} className="sub-account-checkbox">
                  <input
                    type="checkbox"
                    value={subAccount.username}
                    checked={selectedAccounts.includes(subAccount.username)}
                    onChange={(e) => handleAccountSelectionChange(subAccount.username, e.target.checked)}
                  />
                  <span>{subAccount.name}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="invite-button"
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faUserPlus} />
              {isLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
      
      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

export default TeamManagement;
